import 'projects/sharedComponents/src/carousel/carousel.scss';

import { Button, Stack, StackProps } from '@mui/material';
import { FC, useRef, useState } from 'react';
import { useEffectOnce, useScroll, useWindowSize } from 'react-use';

import { buildEmbarkCDNUrl } from 'projects/sharedComponents/src/services/links/linkBuilder';
import clsx from 'clsx';

export type CarouselProps = {
  className?: string;
  spacing?: number;
  stackProps?: StackProps;
  scrollOptions: {
    /** The number of pixels to scroll when clicking one of the arrows */
    pixelsToScroll?: number;
    /** Used to scroll a percentage of the carousel width (e.x. 100 will scroll a full page of items, 50 will scroll half way through the displayed items) */
    percentageToScroll?: number;
    /**
     * Scrolls a single page of items. E.x. if 3 items are visible in the carousel, this will scroll by 3 items when the arrow is pressed.
     * NOTE! The child components must have a single parent HTML element for this to work.
     */
    scrollOnePage?: boolean;
  };
};

/**
 * A carousel with left/right arrows that allows scrolling through the list of children.
 */
export const Carousel: FC<CarouselProps> = (props) => {
  const {
    stackProps,
    spacing = 4,
    className,
    scrollOptions: { percentageToScroll, pixelsToScroll, scrollOnePage },
    children,
  } = props;

  // Forces the component to re-render when the window size changes. Used to control visibility of the left/right buttons.
  const {} = useWindowSize();

  const [, setIsRendered] = useState(false);

  const stackRef = useRef<HTMLDivElement>(null);

  const divElement = stackRef.current;

  // Forces the component to re-render when scrolling, used to control the visibility of the left/right buttons.
  const { x: scrollX } = useScroll(stackRef);

  /** True if there are more child elements than will fit on the page and there is a scroll bar. This is recalculated when the page is resized due to the `useWindowSize` call above. */
  const isOverflowing = !!divElement
    ? divElement.scrollHeight > divElement.clientHeight || divElement.scrollWidth > divElement.clientWidth
    : false;

  // Force the component to re-render once so we can get the stackRef.
  // Without this it will be null, isOverflowing will be false, and the buttons will not show up.
  useEffectOnce(() => {
    setIsRendered(true);
  });

  function onScroll(direction: 'left' | 'right'): void {
    // Shouldn't ever happen but just do nothing.
    if (!divElement) {
      return;
    }

    if (!!pixelsToScroll) {
      scrollByPixels(divElement, direction, pixelsToScroll);
      return;
    }

    if (!!percentageToScroll) {
      scrollByPercentage(divElement, direction, percentageToScroll);
      return;
    }

    if (!!scrollOnePage) {
      scrollNextItemsIntoView(divElement, direction);
      return;
    }
  }

  function onClickLeftArrow(): void {
    onScroll('left');
  }

  function onClickRightArrow(): void {
    onScroll('right');
  }

  // The left button is visible if the carousel is scrolled and has enough products to overflow.
  // Both buttons are hidden on mobile in the CSS
  const isLeftButtonVisible = isOverflowing && !!divElement && scrollX > 0;

  // The right button is visible if there are enough products to scroll and we aren't scrolled all the way to the right.
  // Both buttons are hidden on mobile in the CSS
  const isRightButtonVisible =
    isOverflowing && !!divElement && divElement.clientWidth + scrollX < divElement.scrollWidth;

  return (
    <div className={clsx('carousel-container', className)}>
      <Stack
        direction="row"
        spacing={spacing}
        {...stackProps}
        ref={stackRef}
        className={clsx('carousel-stack', stackProps?.className)}
      >
        {children}
      </Stack>

      <Button
        variant="text"
        className="carousel-leftArrowButton"
        onClick={onClickLeftArrow}
        style={{ display: !isLeftButtonVisible ? 'none' : undefined }}
        tabIndex={-1}
      >
        <img
          className="carousel-leftArrow"
          src={buildEmbarkCDNUrl('/img/icons/arrow-left-carousel.svg')}
          alt="Scroll Products Left"
        />
      </Button>

      <Button
        variant="text"
        className="carousel-rightArrowButton"
        onClick={onClickRightArrow}
        style={{ display: !isRightButtonVisible ? 'none' : undefined }}
        tabIndex={-1}
      >
        <img
          className="carousel-rightArrow"
          src={buildEmbarkCDNUrl('/img/icons/arrow-right-carousel.svg')}
          alt="Scroll Products Right"
        />
      </Button>
    </div>
  );
};

/**
 * Scrolls the carousel by the given number of pixels in the given direction.
 */
function scrollByPixels(elementToScroll: HTMLElement, direction: 'left' | 'right', pixelsToScroll: number): void {
  if (direction === 'left') {
    pixelsToScroll = pixelsToScroll * -1;
  }

  elementToScroll.scrollTo({
    left: elementToScroll.scrollLeft + pixelsToScroll,
    behavior: 'smooth',
  });
}

/**
 * Scrolls the carousel by the given percentage of the total visible width, in the format of `1-100`.
 *
 * This percentage is relative to the visible width of the Carousel, NOT the total width.
 * E.x. 100% will scroll one full page each time the button is clicked.
 */
function scrollByPercentage(
  elementToScroll: HTMLElement,
  direction: 'left' | 'right',
  percentageToScroll: number
): void {
  const pct = percentageToScroll / 100;

  const pixels = elementToScroll.clientWidth * pct;

  scrollByPixels(elementToScroll, direction, pixels);
}

/**
 * Scrolls the next page of items into view.
 *
 * The difference between this function and `scrollByPercentage` is that this takes into account the width of the child elements and tries
 * to guarantee that the child elements are fully in view.
 * E.x. scrolling to the left means that the child elements on the left will not be cut off and will be flush against the left of the carousel.
 */
function scrollNextItemsIntoView(divParentElement: HTMLDivElement, direction: 'left' | 'right'): void {
  const elementArray = Array.from(divParentElement.childNodes);

  if (direction === 'left') {
    elementArray.reverse();
  }

  for (const childNode of elementArray) {
    if (!isHtmlElement(childNode)) {
      continue;
    }

    if (direction === 'right') {
      if (!isRightOfElementInView(divParentElement, childNode)) {
        const childLeft = getLeftOfChildRelativeToParent(childNode);

        divParentElement.scrollTo({
          left: childLeft,
          behavior: 'smooth',
        });

        break;
      }
    }

    if (direction === 'left') {
      if (!isLeftOfElementInView(divParentElement, childNode)) {
        const childRight = getRightOfChildRelativeToParent(childNode);

        divParentElement.scrollTo({
          left: childRight - divParentElement.clientWidth,
          behavior: 'smooth',
        });

        break;
      }
    }
  }
}

function getRightOfChildRelativeToParent(child: HTMLElement): number {
  const childRect = child.getBoundingClientRect();

  return child.offsetLeft + childRect.width;
}

function getLeftOfChildRelativeToParent(child: HTMLElement): number {
  return child.offsetLeft;
}

function isRightOfElementInView(parent: HTMLElement, child: HTMLElement): boolean {
  const childRight = getRightOfChildRelativeToParent(child);

  return childRight <= parent.scrollLeft + parent.clientWidth;
}

function isLeftOfElementInView(parent: HTMLElement, child: HTMLElement): boolean {
  const childLeft = getLeftOfChildRelativeToParent(child);

  return childLeft >= parent.scrollLeft;
}

function isHtmlElement(node: ChildNode): node is HTMLElement {
  return (node as HTMLElement).getBoundingClientRect != null;
}
